import { appendRequestParams, getCiamAuthorizeRequestParams } from '@api/utils';
import { useMemo, useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { createStateParam, isTokenExpired } from '@utils/auth';
import { usePkceGenerator } from '@utils/hooks/usePkceGenerator';
import { Urls } from '@api/const';
import {
  LocalStorageKeys,
  SessionStorageKeys,
  OneTrustScriptProps,
} from '@commons/consts';
import TagManager from 'react-gtm-module';
import { FullPageLoader } from '@components/FullPageLoader';

import { fetchTokenByRefresh } from '@api/auth/auth';
import { logException, logEvent } from '@configs/azureAppInsights/utils';
import { AppMainLayout } from './AppMainLayout';
import { GeneralError } from './pages';

const App = () => {
  const { pkce, error } = usePkceGenerator();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isRefreshFinalized, setIsRefreshFinalized] = useState(true);

  useEffect(() => {
    // After logout we navigate away with window.location.assign(). If the
    // browser serves the return trip (e.g. pressing "back") from bfcache
    // instead of actually reloading, React never re-runs its auth checks,
    // so the frozen, still-authenticated page briefly reappears even
    // though the token is already gone. Force a real reload in that case.
    const handlePageShow = (pageTransitionEvent: PageTransitionEvent) => {
      if (pageTransitionEvent.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    const loadTrackingScripts = () => {
      if (sessionStorage.getItem(SessionStorageKeys.blockTracking) === 'true')
        return;

      const oneTrustScript = document.createElement('script');
      oneTrustScript.src = OneTrustScriptProps.src;
      oneTrustScript.type = 'text/javascript';
      oneTrustScript.setAttribute('data-document-language', 'true');
      oneTrustScript.setAttribute('charset', 'UTF-8');
      oneTrustScript.setAttribute(
        'data-domain-script',
        OneTrustScriptProps.dataDomainScript,
      );
      document.head.appendChild(oneTrustScript);

      const oneTrustInitScript = document.createElement('script');
      oneTrustInitScript.type = 'text/javascript';
      oneTrustInitScript.textContent = 'function OptanonWrapper() {}';
      document.head.appendChild(oneTrustInitScript);

      const gtmScript = document.createElement('script');
      gtmScript.innerHTML = `
        (function(w,d,s,l,i){
          w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
          j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-T9G7P8T');
      `;
      document.head.appendChild(gtmScript);

      TagManager.initialize({ gtmId: 'GTM-T9G7P8T' });
    };

    const triggerLazyLoad = () => {
      loadTrackingScripts();
      window.removeEventListener('scroll', triggerLazyLoad);
      window.removeEventListener('click', triggerLazyLoad);
      window.removeEventListener('keydown', triggerLazyLoad);
      window.removeEventListener('mousemove', triggerLazyLoad);
    };

    window.addEventListener('scroll', triggerLazyLoad, { once: true });
    window.addEventListener('click', triggerLazyLoad, { once: true });
    window.addEventListener('keydown', triggerLazyLoad, { once: true });
    window.addEventListener('mousemove', triggerLazyLoad, { once: true });
  }, []);

  useEffect(() => {
    const isMobileParam = searchParams.get('isHeatMobileApp') === 'true';
    const blockTrackingParam = searchParams.get('blockTracking') === 'true';

    if (!sessionStorage.getItem(SessionStorageKeys.isMobile)) {
      sessionStorage.setItem(
        SessionStorageKeys.isMobile,
        isMobileParam.toString(),
      );
    }

    if (!sessionStorage.getItem(SessionStorageKeys.blockTracking)) {
      sessionStorage.setItem(
        SessionStorageKeys.blockTracking,
        blockTrackingParam.toString(),
      );
    }

    const refreshToken = localStorage.getItem(LocalStorageKeys.refreshToken);
    if (isMobileParam && !!refreshToken) {
      setIsRefreshFinalized(false);
      fetchTokenByRefresh(refreshToken)
        .then(response => {
          localStorage.setItem(LocalStorageKeys.idToken, response.id_token);
          localStorage.setItem(
            LocalStorageKeys.refreshToken,
            response.refresh_token,
          );
          sessionStorage.setItem(
            SessionStorageKeys.token,
            response.access_token,
          );
        })
        .catch(e => {
          logException(`Error while fetching token by refresh token. ${e}`);
          sessionStorage.removeItem(SessionStorageKeys.token);
        })
        .finally(() => {
          setIsRefreshFinalized(true);
        });
    }
  }, []);

  const url = useMemo(() => {
    if (!pkce) return undefined;
    const state = createStateParam(location.pathname);
    const isMobileValue = sessionStorage.getItem(SessionStorageKeys.isMobile);
    const isMobile = !!isMobileValue && isMobileValue === 'true';
    const params = getCiamAuthorizeRequestParams(
      pkce.challenge,
      state,
      isMobile,
    );

    sessionStorage.setItem(SessionStorageKeys.codeVerifier, pkce.verifier);
    sessionStorage.setItem(SessionStorageKeys.state, state);

    return appendRequestParams(Urls.ciamAuthorize, params);
  }, [pkce]);

  let token = sessionStorage.getItem(SessionStorageKeys.token);
  if (token && isTokenExpired(token)) {
    // Known-expired token - drop it now instead of rendering the full
    // dashboard and letting every widget's API call fail with a 401 first.
    sessionStorage.removeItem(SessionStorageKeys.token);
    token = null;
  }

  if (token && isRefreshFinalized) {
    return <AppMainLayout />;
  }

  // Mobile refresh-token exchange still in flight - avoid rendering any
  // conclusion (authenticated layout, error, or redirect) until it settles.
  if (!isRefreshFinalized) {
    return <FullPageLoader />;
  }

  if (error) {
    return <GeneralError />;
  }

  // PKCE challenge is still being generated - this is not an error, just a
  // brief loading window before the CIAM redirect URL can be built.
  if (!url) {
    return <FullPageLoader />;
  }

  const isMobileValue = sessionStorage.getItem(SessionStorageKeys.isMobile);
  const isMobile = !!isMobileValue && isMobileValue === 'true';
  const refreshTokenValue = localStorage.getItem(LocalStorageKeys.refreshToken);
  const refreshToken = refreshTokenValue || 'undefined';
  logEvent(
    `CIAM redirect - Is Mobile: ${isMobile}, Refresh Token: ${refreshToken}`,
  );
  window.location.replace(url);

  // Navigation via window.location.replace is asynchronous - render a
  // loader instead of falling through to the authenticated layout so the
  // header/nav don't flash before the browser leaves the page.
  return <FullPageLoader />;
};

export default App;
